import React, { useCallback, useEffect, useRef, useState } from "react";
import Loader from "../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import notify from "../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import {
  CHECK_IN_TYPEAHEAD_MIN_LEN,
  lookupAppointmentForCheckIn,
  lookupAppointmentForCheckInWithPartialFallback,
  searchCheckInShipmentSuggestions,
} from "./yardCheckInLookup";
import { getOfficeCode, formatTrailerOfficeCodesParam } from "./api";
import { buildManualCheckInDraft } from "./checkInGateUtils";

const TYPEAHEAD_DEBOUNCE_MS = 300;

function isNumericQuery(value) {
  return /^\d+$/.test(String(value ?? "").trim());
}

function suggestionLabel(item) {
  const parts = [item.shipmentNumber];
  if (item.carrierName) parts.push(item.carrierName);
  if (item.status) parts.push(item.status);
  return parts.join(" · ");
}

/**
 * Search for appointment / shipment / gate details, then hand off to `onOpenCheckIn`
 * (opens the full check-in form as its own yard tab, matching how a trailer's detail
 * screen opens — see yardmanagement.js) rather than rendering the form itself.
 */
export function YardCheckInSearchModal({ open, onClose, onOpenCheckIn }) {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [pickList, setPickList] = useState([]);

  const inputRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);
  const suggestAbortRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setLoading(false);
      setSuggestions([]);
      setSuggestionsLoading(false);
      setSuggestionsOpen(false);
      setActiveIdx(-1);
      setPickList([]);
    }
  }, [open]);

  const resetSuggestions = useCallback(() => {
    setSuggestions([]);
    setSuggestionsOpen(false);
    setActiveIdx(-1);
  }, []);

  const runLookup = useCallback(
    async (rawValue, { fromSuggestion = false, officeCodeOverride } = {}) => {
      const raw = String(rawValue ?? "").trim();
      if (!raw) {
        notify.error({
          title: "Required",
          message: "Enter gate details ID, appointment ID, or shipment number.",
        });
        return;
      }

      setLoading(true);
      setPickList([]);

      try {
        let data = null;
        let ambiguous = [];

        if (fromSuggestion) {
          // A picked suggestion carries its OWN office — a multi-office user's currently
          // "active" office (getOfficeCode()) may not be the one this specific shipment
          // belongs to, so prefer the suggestion's office when we have it.
          data = await lookupAppointmentForCheckIn(raw, officeCodeOverride || getOfficeCode());
        } else {
          const result = await lookupAppointmentForCheckInWithPartialFallback(
            raw,
            officeCodeOverride || getOfficeCode()
          );
          data = result.appointment;
          ambiguous = result.suggestions;
        }

        if (!data?.appointmentId && ambiguous.length > 1) {
          setPickList(ambiguous);
          notify.info({
            title: "Multiple matches",
            message: "Select the shipment below to continue check-in.",
          });
          return;
        }

        if (!data?.appointmentId) {
          // LGX-1101: walk-up freight is the dominant case here — most inbound drops have no
          // pre-existing appointment. A zero-result search means "proceed in manual entry",
          // so hand the form a blank draft carrying the searched term and get out of the way.
          // Deliberately silent: no toast, immediate hand-off.
          resetSuggestions();
          onOpenCheckIn?.(buildManualCheckInDraft(raw));
          onClose?.();
          return;
        }

        resetSuggestions();
        onOpenCheckIn?.(data);
        onClose?.();
      } catch (err) {
        console.error(err);
        if (err?.message === "YMS_GATE_LOOKUP_FORBIDDEN") {
          notify.error({
            title: "Permission required",
            message:
              err?.response?.data?.message ||
              "This lookup requires permission, or eligibility override (YMS:OVERRIDE_POST) when skipping gate checks.",
          });
          return;
        }
        if (err?.code === "MISSING_OFFICE_CODE" || err?.message === "MISSING_OFFICE_CODE") {
          notify.error({
            title: "Office required",
            message: "Office code is missing. Select an office or sign in again.",
          });
          return;
        }
        notify.error({
          title: "Lookup failed",
          message:
            err?.response?.data?.message ||
            "Could not load details. Try again.",
        });
      } finally {
        setLoading(false);
      }
    },
    [resetSuggestions, onOpenCheckIn, onClose]
  );

  const handleLookup = useCallback(() => {
    const raw = query.trim();
    if (
      suggestionsOpen &&
      activeIdx >= 0 &&
      activeIdx < suggestions.length &&
      suggestions[activeIdx]
    ) {
      const picked = suggestions[activeIdx];
      setQuery(picked.shipmentNumber);
      runLookup(picked.shipmentNumber, { fromSuggestion: true, officeCodeOverride: picked.officeCode });
      return;
    }
    runLookup(raw);
  }, [query, suggestionsOpen, activeIdx, suggestions, runLookup]);

  const handlePickSuggestion = useCallback(
    (item) => {
      const shipmentNumber = String(item?.shipmentNumber ?? "").trim();
      if (!shipmentNumber) return;
      setQuery(shipmentNumber);
      resetSuggestions();
      runLookup(shipmentNumber, { fromSuggestion: true, officeCodeOverride: item?.officeCode });
    },
    [runLookup, resetSuggestions]
  );

  useEffect(() => {
    if (!open) return undefined;

    const q = query.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (q.length < CHECK_IN_TYPEAHEAD_MIN_LEN) {
      resetSuggestions();
      setSuggestionsLoading(false);
      return undefined;
    }

    debounceRef.current = window.setTimeout(() => {
      if (suggestAbortRef.current) suggestAbortRef.current.abort();
      const controller = new AbortController();
      suggestAbortRef.current = controller;

      setSuggestionsLoading(true);
      // Typeahead must search across ALL of the user's offices, not just the currently
      // "active" one — otherwise shipments under a different office (e.g. DIAMOND-DUM for a
      // user whose active office is DIAMOND-FRO) silently never appear.
      searchCheckInShipmentSuggestions(q, formatTrailerOfficeCodesParam())
        .then((list) => {
          if (controller.signal.aborted) return;
          setSuggestions(list);
          setSuggestionsOpen(list.length > 0);
          setActiveIdx(list.length ? 0 : -1);
        })
        .catch(() => {
          if (!controller.signal.aborted) resetSuggestions();
        })
        .finally(() => {
          if (!controller.signal.aborted) setSuggestionsLoading(false);
        });
    }, TYPEAHEAD_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open, resetSuggestions]);

  useEffect(() => {
    return () => {
      if (suggestAbortRef.current) suggestAbortRef.current.abort();
    };
  }, []);

  const closeAll = () => {
    setQuery("");
    setPickList([]);
    resetSuggestions();
    onClose?.();
  };

  if (!open) return null;

  const showTypeahead =
    suggestionsOpen && (suggestions.length > 0 || suggestionsLoading);
  const numericHint =
    isNumericQuery(query.trim()) && query.trim().length >= CHECK_IN_TYPEAHEAD_MIN_LEN;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4 font-roboto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="yard-checkin-search-title"
      onClick={closeAll}
    >
      <div
        className="bg-white dark:bg-acs-dark-surface rounded-lg w-full max-w-lg shadow-xl border border-gray-200 dark:border-acs-dark-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-acs-dark-border">
          <h2
            id="yard-checkin-search-title"
            className="text-base font-semibold text-gray-900 dark:text-acs-dark-text"
          >
            Check In — search appointment / shipment
          </h2>
          <button
            type="button"
            onClick={closeAll}
            className="text-gray-400 hover:text-gray-700 text-xl leading-none px-2 py-1"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="p-5">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Enter a gate details ID, appointment ID, or part of a shipment number.
            Matching shipments appear as you type.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPickList([]);
                }}
                onFocus={() => {
                  if (suggestions.length) setSuggestionsOpen(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    if (!suggestions.length) return;
                    setSuggestionsOpen(true);
                    setActiveIdx((idx) =>
                      idx < suggestions.length - 1 ? idx + 1 : 0
                    );
                    return;
                  }
                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    if (!suggestions.length) return;
                    setSuggestionsOpen(true);
                    setActiveIdx((idx) =>
                      idx > 0 ? idx - 1 : suggestions.length - 1
                    );
                    return;
                  }
                  if (e.key === "Escape") {
                    resetSuggestions();
                    return;
                  }
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleLookup();
                  }
                }}
                placeholder="Gate ID, appointment ID, or shipment #"
                className="w-full border border-gray-300 dark:border-acs-dark-border rounded-md px-3 py-2 text-sm bg-white dark:bg-acs-dark-surface"
                disabled={loading}
                autoComplete="off"
                aria-autocomplete="list"
                aria-expanded={showTypeahead}
                aria-controls="yard-checkin-suggestions"
              />
              {showTypeahead && (
                <ul
                  id="yard-checkin-suggestions"
                  ref={listRef}
                  role="listbox"
                  className="absolute z-20 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-md border border-gray-200 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface shadow-lg"
                >
                  {suggestionsLoading && !suggestions.length && (
                    <li className="px-3 py-2 text-xs text-gray-500">Searching…</li>
                  )}
                  {numericHint && (
                    <li
                      role="option"
                      aria-selected={activeIdx === -1}
                      className="px-3 py-2 text-xs text-gray-500 border-b border-gray-100 dark:border-acs-dark-border"
                    >
                      Press Search to try appointment ID {query.trim()}
                    </li>
                  )}
                  {suggestions.map((item, idx) => (
                    <li
                      key={item.shipmentNumber}
                      role="option"
                      aria-selected={activeIdx === idx}
                      className={[
                        "px-3 py-2 text-sm cursor-pointer",
                        activeIdx === idx
                          ? "bg-[#1e2a74]/10 text-[#1e2a74] dark:text-[#31C4AB]"
                          : "text-gray-800 dark:text-acs-dark-text hover:bg-gray-50 dark:hover:bg-acs-dark-surface/80",
                      ].join(" ")}
                      onMouseEnter={() => setActiveIdx(idx)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handlePickSuggestion(item)}
                    >
                      <span className="font-medium">{item.shipmentNumber}</span>
                      {(item.carrierName || item.status) && (
                        <span className="block text-xs text-gray-500 dark:text-acs-dark-subtext truncate">
                          {[item.carrierName, item.status].filter(Boolean).join(" · ")}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <button
              type="button"
              onClick={handleLookup}
              disabled={loading}
              className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-[#1e2a74] hover:opacity-90 disabled:opacity-50 shrink-0"
            >
              {loading ? "Searching…" : "Search"}
            </button>
          </div>

          {pickList.length > 1 && (
            <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800/50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-200 mb-2">
                Multiple shipments matched — select one
              </p>
              <ul className="space-y-1 max-h-40 overflow-y-auto">
                {pickList.map((item) => (
                  <li key={item.shipmentNumber}>
                    <button
                      type="button"
                      className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-amber-100/80 dark:hover:bg-amber-900/30"
                      onClick={() => handlePickSuggestion(item)}
                    >
                      {suggestionLabel(item)}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {loading && (
            <div className="flex justify-center py-8">
              <Loader
                color="text-blue-600 dark:text-acs-dark-text"
                size="h-6 w-6"
                message="Loading…"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
